import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from "@material-ui/core";
import { FC, ReactNode, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { NotificationsOutlined, DownloadOutlined, KeyboardArrowDown, KeyboardArrowUp, Tune } from "@mui/icons-material";
import { openModal } from "actions/modalActions";
import { CustomButton } from "componentsNew/buttons/CustomButton";
import { CustomDrawer } from "componentsNew/drawers/CustomDrawer";
import { PayrollDrawerContent } from "componentsNew/drawers/drawerContent/PayrollDrawerContent";
import { AutoSearchInput } from "componentsNew/elements/AutoSearchInput";
import { RoleSelect, StationSelect, StatusSelect } from "componentsNew/elements/SelectField";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import useResize from "hooks/useResize";
import { useStyles } from "./styles";
import { getPayrollData, payrollSetFilter, sendPayrollSendMessagesAction } from "actions/payrollAction";
import { DateFilter } from "componentsNew/elements/DateFilter";
import { FilterSwitch } from "componentsNew/elements/TabSwitch";
import { PayrollGenerationModal } from "componentsNew/modals/PayrollGenerationModal";
import { PayrollUserDateList } from "types/Payroll";
import moment from "moment";

interface ActionItem {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

const PayrollActionsMenu: FC<{ isCompact?: boolean }> = ({ isCompact }) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClose = () => setAnchorEl(null);
  const handleOpenModal = (modalName: string) => {
    handleClose();
    dispatch(openModal(modalName));
  };

  const actionItems: ActionItem[] = [
    {
      key: "payroll-download-table",
      label: "Download Table",
      icon: null,
      onClick: () => handleOpenModal("payroll-download-table-modal"),
    },
    {
      key: "payroll-download-all",
      label: "Download All Shifts",
      icon: null,
      onClick: () => handleOpenModal("payroll-download-all-modal"),
    },
  ];

  return (
    <>
      <CustomButton
        buttonText={"Download"}
        onClick={(event: any) => setAnchorEl(event?.currentTarget)}
        variant="contained"
        icon={<DownloadOutlined />}
        secondIcon={anchorEl ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
      />
      <Menu
        id="payroll-actions-menu"
        anchorEl={anchorEl}
        elevation={0}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {actionItems.map((item) => (
          <MenuItem key={item.key} onClick={item.onClick} disabled={item.disabled}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const PayrollSMSMenu: FC<{ isCompact?: boolean, selctedUserDatesList:PayrollUserDateList[] }> = ({ isCompact, selctedUserDatesList }) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClose = () => setAnchorEl(null);

  const actionItems: ActionItem[] = [
    {
      key: "payroll-sms-type-one",
      label: "Over Clock-In/Out",
      icon: null,
      onClick: () => dispatch(sendPayrollSendMessagesAction()),
    },
    {
      key: "payroll-sms-type-two",
      label: "Missing Clock-In/Out",
      icon: null,
      onClick: () => dispatch(sendPayrollSendMessagesAction(true)),
    },
  ];

  return (
    <>
      <CustomButton
        buttonText={"Notify"}
        onClick={(event: any) => setAnchorEl(event?.currentTarget)}
        variant="outlined"
        theme="blue"
        icon={<NotificationsOutlined />}
        secondIcon={anchorEl ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        disabled={!selctedUserDatesList.length}
      />
      <Menu
        id="payroll-sms-menu"
        anchorEl={anchorEl}
        elevation={0}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {actionItems.map((item) => (
          <MenuItem key={item.key} onClick={item.onClick} disabled={item.disabled}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const PayrollFilterFields: FC = () => {
  const dispatch = useDispatch();
  const { payrollFilters } = useTypeSelectors((state) => state.payrollTable);

  return (
    <>
      <StationSelect
        value={payrollFilters.station ? Number(payrollFilters.station) : 0}
        onChange={(value: string) => dispatch(payrollSetFilter({ station: Number(value) }))}
        hideLabel
      />
      <StatusSelect
        value={payrollFilters.userStatus ? Number(payrollFilters.userStatus) : 0}
        onChange={(value: string) => dispatch(payrollSetFilter({ userStatus: Number(value) }))}
        hideLabel
      />
      <RoleSelect
        fullWidth
        value={payrollFilters.userRole ? Number(payrollFilters.userRole) : 0}
        onChange={(value: string) => dispatch(payrollSetFilter({ userRole: Number(value) }))}
        hideLabel
      />
    </>
  );
};

const PayrollHead: FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { width } = useResize();
  const { payrollFilters, selctedUserDatesList, userList } = useTypeSelectors(
    (state) => state.payrollTable,
  );
  const isMobile = width <= 840;
  const isCompact = width <= 1280;

  useEffect(() => {
    const params = {
      ...(payrollFilters.type === "week" && {
        week: Number(payrollFilters?.week_date?.split("-W")[1]) - 1,
        year_number: Number(payrollFilters?.week_date?.split("-W")[0]),
      }),
      ...(payrollFilters.type === "month" && {
        month: payrollFilters.month,
        year_number: payrollFilters.year,
      }),
      ...((payrollFilters.type === "two_week" || payrollFilters.type === "week_range") && {
        date_from: payrollFilters.rangeDateOne,
        date_to: payrollFilters.rangeDateTwo,
      }),
      is_active_offboarded: true,
    };
    dispatch(getPayrollData(params));
  }, [
    payrollFilters?.week_date,
    dispatch,
    payrollFilters.rangeDateOne,
    payrollFilters.rangeDateTwo,
    payrollFilters.type,
    payrollFilters.month,
    payrollFilters.year,
  ]);

  const setWeekFilter = (value: string) => {
    const newWeekValue = value ? value : `${moment().format("yyyy")}-W${moment().format("W")}`
    dispatch(payrollSetFilter({ week_date: newWeekValue }))
  }

  const setWeekRnageOneFilter = (value: string) => {
    const newWeekValue = value ? value : `${moment().format("yyyy")}-W${moment().format("W")}`
    dispatch(payrollSetFilter({ rangeDateOne: newWeekValue }))
  }

  const setWeekRnageTwoFilter = (value: string) => {
    const newWeekValue = value ? value : `${moment().format("yyyy")}-W${moment().format("W")}`
    dispatch(payrollSetFilter({ rangeDateTwo: newWeekValue }))
  }

  const dateFilter = (
    <DateFilter
      type={payrollFilters.type}
      weekValue={payrollFilters.week_date}
      weekChange={(value) => setWeekFilter(value)}
      weekRangeOneValue={payrollFilters.rangeDateOne}
      weekRangeTwoValue={payrollFilters.rangeDateTwo}
      weekRangeOneChange={(value) => setWeekRnageOneFilter(value)}
      weekRangeTwoChange={(value) => setWeekRnageTwoFilter(value)}
      monthValue={`${payrollFilters.year}-${String(payrollFilters.month).padStart(2, "0")}`}
      monthChange={(value) => {
        const [year, month] = value.split("-");
        dispatch(payrollSetFilter({ year, month: String(Number(month)) }));
      }}
      hasSwitch
      keepSwitchOnMobile
      fullWidth={isMobile}
    />
  );

  const downloadActions = (
    <Box className={classes.rightBox}>
      <PayrollSMSMenu isCompact={isCompact} selctedUserDatesList={selctedUserDatesList} />
      <PayrollActionsMenu isCompact={isCompact} />
      <PayrollGenerationModal incomingModalName={"payroll-download-table-modal"} applyFilter />
      <PayrollGenerationModal incomingModalName={"payroll-download-all-modal"} />
    </Box>
  );

  return (
    <Box className={classes.paper}>
      <Box className={classes.headBox}>
        <Box className={classes.leftBox}>
          <FilterSwitch
            type={payrollFilters.type}
            enabledFilters={["month", "week", "week_range"]}
            action={(value) => dispatch(payrollSetFilter({ type: value }))}
          />
          {!isMobile && dateFilter}
        </Box>
      </Box>
      {isMobile && <Box className={classes.headBox}>{dateFilter}</Box>}
      <Box className={classes.headBox}>
        <Box className={classes.leftBox}>
          {!isMobile && <PayrollFilterFields />}
          <Box className={classes.searchBox}>
            <AutoSearchInput
              value={payrollFilters.user}
              options={userList}
              onChange={(user) => dispatch(payrollSetFilter({ user }))}
              getOptionLabel={(user) => user?.username || ""}
              getOptionSelected={(option, selected) => option.id === selected.id}
              placeholder={"User"}
            />
          </Box>
          {isMobile && (
            <CustomDrawer
              customWidth={300}
              buttonType={"custom"}
              drawer_name={"payroll-filters-drawer"}
              drawerTitle={"Filters"}
              disableMobileFullWidth
              customButtonElement={
                <Box className={classes.filterIconBtn}>
                  <CustomButton buttonText="" onClick={() => null} variant="outlined" icon={<Tune />} />
                </Box>
              }
            >
              <PayrollDrawerContent />
            </CustomDrawer>
          )}
        </Box>
        {!isMobile && downloadActions}
      </Box>
      {isMobile && downloadActions}
    </Box>
  );
};

export default PayrollHead;
